import { useEffect, useState } from 'preact/hooks'
import { crmSuppressionSourcesApi } from '@/api/crmSuppressionSources'
import type { CrmPickerOptionDto, CrmSuppressionSourceKind } from '@/types/crmSuppressionSources'

/**
 * Loads the choices for whichever picker the chosen source kind needs — HubSpot lists, Salesforce
 * campaigns, or Salesforce fields.
 *
 * Three outcomes, not two, and the difference between them is the point of this hook:
 *
 * - `permissionDenied` — the CRM REFUSED the read, most often a connection made before the
 *   list-reading scope was requested. Reconnecting fixes it, so the caller says so.
 * - `error` — the read FAILED. Reconnecting cannot fix a 500, and telling someone to try sends them
 *   round a loop that leaves them exactly where they started.
 * - neither, with no options — the CRM answered and has nothing to offer. The caller says that
 *   plainly. Advising a reconnect here is the bug this shape exists to prevent: it is the one case
 *   where reconnecting can change nothing at all, so it reads as "your reconnect did not work".
 */
export function useCrmPickerOptions(clientCompanyId: string, kind: CrmSuppressionSourceKind) {
  const [options, setOptions] = useState<CrmPickerOptionDto[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [permissionDenied, setPermissionDenied] = useState(false)

  // The standard opt-out reads a fixed field, so it is the one kind with nothing to pick.
  const needsPicker = kind !== 'Salesforce Standard Opt Out'

  useEffect(() => {
    setOptions([])
    setError(null)
    setPermissionDenied(false)
    // Every sibling hook guards this. Without it the picker calls `/api/ClientCompanies//HubSpot/Lists`,
    // which is a different route entirely, so the failure never names the real cause.
    if (!clientCompanyId) {
      setLoading(false)
      return
    }
    if (!needsPicker) {
      // Cleared, not just skipped. Switching from a kind whose picker was mid-flight to one with no
      // picker at all would otherwise leave loading stuck on, and the next picker kind would open
      // disabled and reading "Loading…" before its own request had even started.
      setLoading(false)
      return
    }

    let cancelled = false
    const fetchOptions = async () => {
      setLoading(true)
      const res =
        kind === 'HubSpot List' ? await crmSuppressionSourcesApi.hubSpotLists(clientCompanyId)
          : kind === 'Salesforce Campaign' ? await crmSuppressionSourcesApi.salesforceCampaigns(clientCompanyId)
          : await crmSuppressionSourcesApi.salesforceSuppressionFields(clientCompanyId)
      // The kind changed (or the modal closed) while this was in flight; drop the result.
      if (cancelled) return
      if (res.error) {
        // The endpoints answer 200 for anything they can describe, so reaching here means the call
        // itself did not land.
        setError(res.error)
        setOptions([])
      } else if (res.data?.Status === 'Permission Denied') {
        setPermissionDenied(true)
        setOptions([])
      } else if (res.data?.Status === 'Failed') {
        setError(res.data.Message ?? 'Please try again.')
        setOptions([])
      } else {
        setOptions(res.data?.Options ?? [])
      }
      setLoading(false)
    }
    void fetchOptions()
    return () => { cancelled = true }
  }, [clientCompanyId, kind, needsPicker])

  return { options, loading, needsPicker, error, permissionDenied }
}
